/**
 * API service for making authenticated HTTP requests to the RILE Connect Agent
 * Separate from the main axiosInstance which targets the Node server
 */

import firebase from './firebase';

// Agent API configuration from environment variables
// Flask REST API runs on a different port from the WebSocket server
const AGENT_BASE_URL =
    process.env.REACT_APP_AGENT_URL || 'http://localhost:5001';

/**
 * Get authentication token from Firebase
 */
async function getAuthToken() {
    const user = firebase.auth.currentUser;
    if (!user) {
        throw new Error('User not authenticated');
    }
    return await user.getIdToken();
}

/**
 * Make an authenticated API request to the agent
 */
async function agentApiRequest(endpoint, options = {}) {
    try {
        const token = await getAuthToken();

        const response = await fetch(`${AGENT_BASE_URL}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                ...options.headers,
            },
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({
                error: `HTTP error! status: ${response.status}`,
            }));
            throw new Error(
                error.error || `HTTP error! status: ${response.status}`
            );
        }

        // Handle 204 No Content
        if (response.status === 204) {
            return null;
        }

        return await response.json();
    } catch (error) {
        console.error('Agent API request failed:', error);
        throw error;
    }
}

/**
 * Agent API methods for different HTTP verbs
 */
export const agentApi = {
    get: (endpoint) => agentApiRequest(endpoint, { method: 'GET' }),

    post: (endpoint, data) =>
        agentApiRequest(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    put: (endpoint, data) =>
        agentApiRequest(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    delete: (endpoint) => agentApiRequest(endpoint, { method: 'DELETE' }),
};

export default agentApi;
